import {WaveLoader} from '@wandb/weave/components/Loaders/WaveLoader';
import {TailwindContents} from '@wandb/weave/components/Tailwind';
import classNames from 'classnames';
import React, {useEffect, useMemo} from 'react';
import {useHistory} from 'react-router-dom';
import {twMerge} from 'tailwind-merge';

import {useSelector} from '../../state/hooks';
import {PanelBankContextProvider} from '../../state/panelbank/context';
import {useRunSelectorResizingContextMaybe} from '../MultiRunWorkspace/RunSelectorResizingContext';
import {useAutoSyncPanelBankContext} from '../PanelBank/AutoSyncPanelBankContext';
import {usePanelBankSectionsScrollContext} from '../PanelBank/PanelBankSectionsScrollContextProvider';
import {PanelCompRedux} from '../PanelCompRedux';
import {useWorkspaceInheritedSettingsForPanel} from '../WorkspaceDrawer/Settings/hooks/useWorkspaceInheritedSettingsForPanel';
import {SectionSettingsContextProvider} from '../WorkspaceDrawer/Settings/SectionSettingsContextProvider';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawer/WorkspaceDrawerContextProvider';
import {FullScreenPanelHeader} from './FullScreenPanelHeader';
import {FullScreenPanelNotFound} from './FullScreenPanelNotFound';
import {useConfigXBounds} from './hooks';
import {PanelContextProvider} from './PanelContextProvider';
import {PanelEditorContainer} from './PanelEditorContainer';
import {PanelIDContextProvider} from './PanelIDContext';
import {SharedPanelStateContextProvider} from './SharedPanelStateContext';
import {getPanelLinkById, useStandalonePanelContext} from './StandalonePanel';

export const FullScreenPanelContainer = ({
  isProjectReadOnly,
  className,
}: {
  isProjectReadOnly: boolean; // whether user can edit project level resources (e.g. reports)
  className?: string;
}) => {
  const {isPanelNotFound, panelRefAndPanelBankSectionConfigRef} =
    useStandalonePanelContext();

  const {isLoading: isAutoSyncLoading} = useAutoSyncPanelBankContext();
  const isLoading = isPanelNotFound && isAutoSyncLoading;

  if (isLoading) {
    return <WaveLoader size="small" />;
  }

  if (isPanelNotFound) {
    return <FullScreenPanelNotFound />;
  }

  const {panelBankSectionConfigRef} = panelRefAndPanelBankSectionConfigRef!;

  return (
    // We need to wrap the FullScreenPanel component in a SectionSettingsContextProvider so
    // inherited settings takes into both workspace and section values
    <SectionSettingsContextProvider sectionRef={panelBankSectionConfigRef}>
      <PanelBankContextProvider>
        <FullScreenPanel
          isProjectReadOnly={isProjectReadOnly}
          className={className}
        />
      </PanelBankContextProvider>
    </SectionSettingsContextProvider>
  );
};
// Make sure the SectionSettingsContextProvider wraps this FullScreenPanel component
const FullScreenPanel = ({
  isProjectReadOnly,
  className,
}: {
  isProjectReadOnly: boolean;
  className?: string;
}) => {
  const history = useHistory();
  const {panelRefAndPanelBankSectionConfigRef} = useStandalonePanelContext();
  const {panelBankSectionConfigRef, panelRef} =
    panelRefAndPanelBankSectionConfigRef!;

  const inheritedSettings = useWorkspaceInheritedSettingsForPanel();
  const configXBounds = useConfigXBounds(panelRef);
  const runSelectorResizingContext = useRunSelectorResizingContextMaybe();
  const {shouldHidePanelContents} = useWorkspaceDrawerContext();
  const shouldRenderContent =
    !shouldHidePanelContents && !runSelectorResizingContext?.isResizing;

  // This ensures that we scroll to the correct section when user clicks
  // on the back button in the full screen panel.
  const panelBankSectionConfigId = useSelector(
    state =>
      state.views.parts[panelBankSectionConfigRef.type][
        panelBankSectionConfigRef.id
      ].__id__
  );
  const {setScrollToPanelBankSectionId} = usePanelBankSectionsScrollContext();

  // This is currently used to track panel setting analytics
  const stablePanelId = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].__id__
  );

  const panelType = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].viewType
  );

  const isPanelAuto = useSelector(
    state => state.views.parts[panelRef.type][panelRef.id].isAuto
  );

  const panelViewTypeClass = useMemo(
    () => panelType.toLowerCase().replace(' ', '-') + '-container',
    [panelType]
  );

  const panelClassNames = useMemo(() => {
    return classNames('editable-panel', panelViewTypeClass, {
      'editable-panel-no-content': !shouldRenderContent,
    });
  }, [panelViewTypeClass, shouldRenderContent]);

  useEffect(() => {
    setScrollToPanelBankSectionId(panelBankSectionConfigId);
  }, [panelBankSectionConfigId, setScrollToPanelBankSectionId]);

  return (
    <PanelContextProvider
      isProjectReadOnly={isProjectReadOnly}
      panelBankSectionConfigRef={panelBankSectionConfigRef}
      panelRef={panelRef}
      inheritedSettings={inheritedSettings}>
      <PanelIDContextProvider stablePanelId={stablePanelId}>
        <SharedPanelStateContextProvider config={configXBounds}>
          <PanelEditorContainer
            currentHeight={0} // TODO: make currentHeight default to 0 in component and make prop optional
            onOK={() => {
              // If panel is auto, then the current URL is based on the panel display name.
              // When a panel is edited, its display name may change (e.g. user updates
              // panel title or y axis metrics). We must redirect to the ID-based URL to
              // avoid ending up on a "panel not found" page. This is safe to do because
              // the panel will no longer be auto once it's edited.
              if (isPanelAuto) {
                history.replace(getPanelLinkById(stablePanelId));
              }
            }}
          />
          <TailwindContents>
            <div
              className={twMerge(panelClassNames, 'px-16 py-12', className)}
              data-test="panel">
              <FullScreenPanelHeader
                panelBankSectionConfigRef={panelBankSectionConfigRef}
                panelType={panelType}
              />
              <div
                className={twMerge(
                  'flex flex-col',
                  'h-full w-full grow-[1]',
                  'justify-center'
                )}
                id={'editable-panel-' + stablePanelId}>
                <PanelCompRedux
                  configMode={false}
                  currentHeight={0}
                  dimensions={undefined as any}
                  panelRef={panelRef}
                />
              </div>
            </div>
          </TailwindContents>
        </SharedPanelStateContextProvider>
      </PanelIDContextProvider>
    </PanelContextProvider>
  );
};
